// pms-core / domain — what a desk cancellation costs the guest (OB3,
// docs/PMS-OWNED-BOOKINGS-BRIEF.md §4).
//
// Per logement: the terms it was SOLD under (PB7b, copied at sale), the base they
// apply to (room nights, board and extras — never the taxe de séjour), and the fee
// those terms retain on the day of the cancellation. Then, for the booking: what
// the desk keeps (prefilled with the fee, editable down to 0) against the deposit
// already received — the difference is what to key into the VAD, or to refund.
//
// Pure: no clock, no database. Nothing here is posted (ARRHES A3–A5 own that).

import { cancellationFee, type SoldTerms } from './cancellationPolicy.js';
import type { Cents, IsoDate } from './types.js';
import type { PmsGuarantee } from './pms.js';

/** One closed-vocabulary reason (`pms_cancellation_reason`, 0138). */
export interface CancellationReason {
  code: string;
  labelFr: string;
  /** « Erreur de saisie »: never a real cancellation — reports exclude it. */
  isCorrection: boolean;
}

/** What one logement's terms apply to (`pms_room_stay_terms_base`). */
export interface StayTermsBase {
  roomStayId: string;
  baseCents: Cents;
}

/** One line of the cancel event's fee record (OB3 Q3). */
export interface CancellationFeeLine {
  roomStayId: string;
  policyCode: string | null;
  feeComputedCents: Cents;
  feeRetainedCents: Cents;
}

/** The logement fields a quote reads. */
export interface QuotedStay {
  id: string;
  checkinDate: IsoDate;
  cancelledAt: string | null;
  cancellationTerms?: SoldTerms | null;
}

export interface StayQuote {
  roomStayId: string;
  terms: SoldTerms | null;
  baseCents: Cents;
  /** What the terms retain if cancelled on `on`. 0 when no terms were copied. */
  feeCents: Cents;
  /** What a no-show would cost under the same terms (information: A5 invoices it). */
  noShowCents: Cents;
}

/** Each LIVE logement's fee on `on`. A released room is not cancelled twice. */
export function cancelQuote(
  stays: readonly QuotedStay[],
  bases: readonly StayTermsBase[],
  on: IsoDate,
): StayQuote[] {
  const base = new Map(bases.map((b) => [b.roomStayId, b.baseCents]));
  return stays
    .filter((s) => s.cancelledAt === null)
    .map((s) => {
      const terms = s.cancellationTerms ?? null;
      const baseCents = base.get(s.id) ?? 0;
      return {
        roomStayId: s.id,
        terms,
        baseCents,
        feeCents: terms === null ? 0 : cancellationFee(terms, s.checkinDate, on, baseCents),
        noShowCents: terms === null ? 0 : Math.round((baseCents * terms.noShowPct) / 100),
      };
    });
}

/** The deposit the PMS knows was received: a VAD keyed or a transfer arrived
 *  (`vadAmountCents`), plus whatever the payment read reports (Vega's, for a
 *  seeded booking — 0 for one the PMS made). */
export function depositReceived(
  guarantee: PmsGuarantee,
  vadAmountCents: Cents | null,
  paidCents: Cents,
): Cents {
  const pms = guarantee === 'vad_done' || guarantee === 'transfer_done' ? (vadAmountCents ?? 0) : 0;
  return pms + paidCents;
}

/** What the desk does with the money: charge the rest through the VAD, refund
 *  the excess, or nothing. Never both. */
export function cancelSettlement(keptCents: Cents, receivedCents: Cents): { chargeCents: Cents; refundCents: Cents } {
  const d = keptCents - receivedCents;
  return { chargeCents: Math.max(d, 0), refundCents: Math.max(-d, 0) };
}

/** Splits what the desk keeps across the logements, never above any one's fee:
 *  each keeps its fee in order until the total is spent. The RPC refuses kept > computed. */
export function feeLines(quote: readonly StayQuote[], keptCents: Cents): CancellationFeeLine[] {
  let left = Math.max(0, Math.round(keptCents));
  return quote.map((q) => {
    const kept = Math.min(q.feeCents, left);
    left -= kept;
    return {
      roomStayId: q.roomStayId,
      policyCode: q.terms?.code ?? null,
      feeComputedCents: q.feeCents,
      feeRetainedCents: kept,
    };
  });
}

/** What « Remettre à zéro » did to the training établissement (OB5). */
export interface TrainingResetReport {
  reservations: number;
  guests: number;
  /** Bookings that reached the fiscal chain (a payment, an invoice): they stay. */
  kept: number;
  currentDay: IsoDate;
}
